import AxiosInstance from '../config/AxiosInstance';

export interface TransferLinePayload {
  inventoryTransferId?: string | null;
  productId: string;
  productSku: string;
  productName: string;
  productUOMId?: string;
  sentQty: number;
  receivedQty: number;
  varianceQty: number;
  /** Per unit of the line, and cost × sent qty. */
  cost?: number;
  lineCost?: number;
}

/** The API sets who sent or received and when; the dates and people here are only kept for older screens. */
export interface TransferPayload {
  comment?: string;
  internal_number?: number;
  company_id: string;
  from_location_id: string;
  to_location_id: string;
  sent_by?: string;
  sent_date?: string;
  received_by?: string;
  received_date?: string;
  reason_id: string | null;
  status_id: number;
  inventoryTransferDetail: TransferLinePayload[];
}

const createTransfer = async (payload: TransferPayload) => {
  try {
    const { data } = await AxiosInstance.post('/api/inventory/inventory-transfers', payload);
    return { data };
  } catch (error) {
    throw error;
  }
};

const updateTransfer = async (id: string, payload: TransferPayload, locationId: string) => {
  try {
    const { data } = await AxiosInstance.put('/api/inventory/inventory-transfers/' + id, payload, {
      params: {
        locationId: locationId
      }
    });
    return { data };
  } catch (error) {
    throw error;
  }
};

const getAllTransfer = async (searchValue: string, type: string, page: string, limit: number, locationId: string) => {
  try {
    const { data } = await AxiosInstance.get('/api/inventory/inventory-transfers/list', {
      params: {
        searchValue: searchValue,
        type: type,
        page: page,
        limit: limit,
        locationId: locationId
      },
    });
    return { data };
  } catch (error) {
    throw error;
  }
};

const deleteTransfer = async (id: string, locationId: string) => {
  try {
    const { data } = await AxiosInstance.delete('/api/inventory/inventory-transfers/' + id, {
      params: {
        locationId: locationId
      }
    });
    return { data };
  } catch (error) {
    throw error;
  }
};

const getDetailTransfer = async (id: string, locationId: string) => {
  try {
    const { data } = await AxiosInstance.get('/api/inventory/inventory-transfers/' + id, {
      params: {
        locationId: locationId
      }
    });
    return { data };
  } catch (error) {
    throw error;
  }
};

export interface TransfersOverviewQuery {
  from?: string;
  to?: string;
  status?: string;
  /** Relative to the site: 'in' = coming to it, 'out' = leaving it. */
  direction?: 'in' | 'out';
  search?: string;
  locationId?: string;
  page: number;
  limit: number;
}

type Named = { id: string; name: string | null };

export interface TransfersOverviewRow {
  id: string;
  internalNumber: number;
  statusId: number;
  from: Named;
  to: Named;
  reason: { id: string; name: string } | null;
  createdAt: string;
  createdBy: Named | null;
  sentAt: string | null;
  sentBy: Named | null;
  receivedAt: string | null;
  receivedBy: Named | null;
  lines: number;
  value: number;
}

export interface TransfersOverview {
  rows: TransfersOverviewRow[];
  count: number;
  page: number;
  limit: number;
  statusCounts: Record<string, number>;
  byRoute: { fromId: string; fromName: string | null; toId: string; toName: string | null; transfers: number; value: number }[];
  totals: { transfers: number; value: number };
}

const getTransfersOverview = async (query: TransfersOverviewQuery) => {
  try {
    const { data } = await AxiosInstance.get('/api/inventory/inventory-transfers/overview', { params: query });
    return { data };
  } catch (error) {
    throw error;
  }
};

export interface TransferLineCost {
    productId: string;
    productUOMId: string | null;
    /** Per unit of the line. */
    cost: number;
    source: 'stock' | 'supplier' | 'none';
    /** Only a 0.00 cost that no stock layer gives is entered by hand. */
    editable: boolean;
}

// The cost each line takes at the sending site: its stock layers (FIFO), else the supplier; receiving stores what was booked.
const getTransferLineCosts = async (payload: { locationId: string; lines: { productId: string; productUOMId?: string; quantity?: number }[] }) => {
    try {
        const { data } = await AxiosInstance.post('/api/inventory/inventory-transfers/costs', payload);
        return { data };
    } catch (error) {
        throw error;
    }
}

export { getTransfersOverview, createTransfer, updateTransfer, getAllTransfer, deleteTransfer, getDetailTransfer, getTransferLineCosts };
